import React, { useEffect, useRef } from 'react'; // Add useRef to imports
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import { api } from "./axiosInstance";
import { readUtmFromLocalStorage, clearUtmStorage } from "../../utils/utmStorage";

const AuthSuccess = ({ onSignInSuccess }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const processedRef = useRef(false); // Latch to prevent loops

    useEffect(() => {
        // If we've already processed this login or there's no token, stop.
        if (processedRef.current) return;

        const token = searchParams.get("token");
        if (!token) return;

        processedRef.current = true; // Set latch immediately

        const processAuth = async () => {
            const userId = searchParams.get("userId");
            const role = searchParams.get("role");
            const isSuperUser = searchParams.get("isSuperUser");

            localStorage.setItem("token", token);
            localStorage.setItem("userId", userId);
            localStorage.setItem("role", role);
            if (isSuperUser) localStorage.setItem("isSuperUser", isSuperUser);

            try {
                const decoded = jwtDecode(token);
                localStorage.setItem("user", JSON.stringify(decoded));

                if (onSignInSuccess) {
                    onSignInSuccess(decoded);
                }

                // Attach UTM attribution from localStorage if present (UTM-05)
                const utmData = readUtmFromLocalStorage();
                if (utmData && (utmData.utmSource || utmData.utmMedium || utmData.utmCampaign || utmData.referrer)) {
                    try {
                        await api.post("/auth/attach-attribution", utmData);
                    } catch (utmErr) {
                        // Non-fatal — attribution is best-effort, do not block login
                        console.warn("[AuthSuccess] UTM attach failed — continuing login", utmErr);
                    }
                    clearUtmStorage();
                }

                // Redirect logic - navigate with { replace: true } handles URL cleanup
                if (role === "Candidate" || role === "Target") {
                    if (!decoded.postalCode || !decoded.country) {
                        navigate("/complete-profile", { replace: true });
                    } else {
                        navigate("/assessment", { replace: true });
                    }
                } else {
                    navigate("/employer-dashboard", { replace: true });
                }
            } catch (err) {
                console.error("Token decode failed", err);
                navigate("/signin", { replace: true });
            }
        };

        processAuth();
    }, [searchParams, navigate, onSignInSuccess]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-white">
            <div className="text-center">
                <h2 className="text-xl font-bold text-orange mb-2">Authenticating...</h2>
                <p className="text-gray-400">Please wait while we log you in.</p>
            </div>
        </div>
    );
};

export default AuthSuccess;